import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { ChevronRight, Plus, Edit, ToggleLeft, ToggleRight, Save, X } from "lucide-react";
import { useAuth } from "../../utils/auth";
import { canManageTicketProblemTypes } from "../../utils/roles";
import {
  fetchTicketProblemTypes,
  createTicketProblemType,
  updateTicketProblemType,
  toggleTicketProblemType,
} from "../../api/TicketAPIs";
import { formatDateTime } from "./ticketUi";

const TicketProblemTypesPage = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canManage = canManageTicketProblemTypes(authState.role);

  const [formMode, setFormMode] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState("");
  const [isActive, setIsActive] = useState(true);

  const { data = [], isLoading, refetch } = useQuery({
    queryKey: ["ticketProblemTypes", "manage"],
    queryFn: () => fetchTicketProblemTypes(false),
    enabled: canManage,
  });

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!authState.isValidating && !canManage) navigate("/login");
  }, [authState, canManage, navigate]);

  const openCreate = () => {
    setFormMode("create");
    setEditingId(null);
    setName("");
    setIsActive(true);
  };

  const openEdit = (item) => {
    setFormMode("edit");
    setEditingId(item.id);
    setName(item.name);
    setIsActive(item.isActive);
  };

  const closeForm = () => {
    setFormMode(null);
    setEditingId(null);
    setName("");
    setIsActive(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) {
      toast.error("الاسم مطلوب");
      return;
    }
    try {
      if (formMode === "create") {
        await createTicketProblemType({ name: name.trim(), isActive });
        toast.success("تم الإنشاء");
      } else {
        await updateTicketProblemType(editingId, { name: name.trim(), isActive });
        toast.success("تم التحديث");
      }
      closeForm();
      refetch();
    } catch (err) {
      toast.error(err.message || "فشل الحفظ");
    }
  };

  const handleToggle = async (id) => {
    try {
      await toggleTicketProblemType(id);
      refetch();
    } catch (err) {
      toast.error(err.message || "فشل التبديل");
    }
  };

  if (!canManage) return null;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 md:p-8" dir="rtl">
      <ToastContainer position="top-center" />
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/tickets")} className="p-2 rounded-xl bg-white shadow">
              <ChevronRight size={22} />
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold">أنواع مشاكل التذاكر</h1>
              <p className="text-gray-500 text-sm">لا يتم الحذف النهائي — استخدم التفعيل/التعطيل</p>
            </div>
          </div>
          <button
            onClick={openCreate}
            className="px-4 py-2 rounded-xl bg-gradient-to-r from-purple-600 to-pink-600 text-white flex items-center gap-2"
          >
            <Plus size={16} /> نوع جديد
          </button>
        </div>

        <div className="bg-white/90 rounded-2xl shadow border border-gray-100 overflow-hidden">
          {isLoading ? (
            <div className="p-8 text-center text-gray-500">جاري التحميل...</div>
          ) : (
            <table className="w-full text-right">
              <thead className="bg-gray-50 text-sm text-gray-600">
                <tr>
                  <th className="p-3">الاسم</th>
                  <th className="p-3">الحالة</th>
                  <th className="p-3">آخر تحديث</th>
                  <th className="p-3">إجراءات</th>
                </tr>
              </thead>
              <tbody>
                {data.map((item) => (
                  <tr key={item.id} className="border-t border-gray-100">
                    <td className="p-3 font-medium">{item.name}</td>
                    <td className="p-3">
                      <span
                        className={`px-2 py-1 rounded-full text-sm ${
                          item.isActive ? "bg-green-100 text-green-700" : "bg-gray-200 text-gray-600"
                        }`}
                      >
                        {item.isActive ? "نشط" : "معطّل"}
                      </span>
                    </td>
                    <td className="p-3 text-sm">{formatDateTime(item.updatedAtUtc)}</td>
                    <td className="p-3">
                      <div className="flex gap-2">
                        <button
                          onClick={() => openEdit(item)}
                          className="p-2 rounded-lg bg-indigo-50 text-indigo-700"
                          title="تعديل"
                        >
                          <Edit size={16} />
                        </button>
                        <button
                          onClick={() => handleToggle(item.id)}
                          className="p-2 rounded-lg bg-gray-50 text-gray-700"
                          title="تفعيل/تعطيل"
                        >
                          {item.isActive ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {formMode && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold">
                {formMode === "create" ? "نوع مشكلة جديد" : "تعديل نوع المشكلة"}
              </h3>
              <button onClick={closeForm}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-sm text-gray-600">الاسم</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full mt-1 px-3 py-2 border-2 border-gray-200 rounded-xl"
                  required
                />
              </div>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                نشط
              </label>
              <button
                type="submit"
                className="w-full py-3 rounded-xl bg-indigo-600 text-white font-semibold flex items-center justify-center gap-2"
              >
                <Save size={16} /> حفظ
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketProblemTypesPage;
